import { useSelector } from 'react-redux';
import type { IPatient } from '@common/interfaces/patient';
import type { PractitionerName } from '@common/interfaces/practitionerName';
import { CareProviderFormInput } from '@frontend/components/elements/careProviderSelector/CareProviderFormInput';
import { GeneralPractitionerSearchWrapper } from '@frontend/components/elements/careProviderSelector/GeneralPractitionerSearchWrapper';
import type { IReduxState } from '../../configureStore';
import type { IOrganization } from '../interfaces/organization';

type CareProviderSelectionProps = {
    automation?: string;
    error?: string;
    formlabeltext?: string;
    hasErrors?: boolean;
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    isActiveGeneralPractitioner?: boolean;
    isInModal?: boolean;
    itemsPerPage?: number;
    onChange: (organizationId: string | null, practitionerId: string | null) => Promise<void>;
    onDoValidate?: () => Promise<void>;
    patient?: IPatient;
    required?: boolean;
    size?: 'medium' | 'large';
};

export type OrganizationPractitionerOptions = {
    disabled: boolean;
    label: string;
    practitioners: {
        label: string;
        value: string;
    }[];
    value: string;
};

type Practitioner = PractitionerName & {
    id: string;
};

export type CareProviderOrganizationsPractitioners = IOrganization & {
    practitioners: Practitioner[];
};

export type InitialOrganizationPractitioner = IOrganization & {
    practitioner: Practitioner;
};

type ComponentVariants = 'formInput' | 'generalPractitionerSearch' | 'none';

export function CareProviderSelection({
    automation,
    error,
    formlabeltext = '',
    hasErrors = false,
    initialOrganizationId,
    initialPractitionerId,
    isInModal,
    itemsPerPage = 5,
    onChange,
    onDoValidate,
    patient,
    required = false,
    size = 'medium',
    isActiveGeneralPractitioner,
}: CareProviderSelectionProps) {
    const loggedInUserInformation = useSelector((state: IReduxState) => state.authentication);

    const {
        isManager: isUserManager,
        organizationId: loggedInUserOrganizationId,
        id: loggedInUserId,
        organizationHasGeneralPractitionerOrSupervisor: loggedInUserHasGeneralPractitionersInOrganization,
    } = loggedInUserInformation;

    const componentToShow = determineComponentToShow(
        isUserManager,
        patient,
        loggedInUserHasGeneralPractitionersInOrganization,
        loggedInUserOrganizationId,
        loggedInUserId,
        isInModal,
        isActiveGeneralPractitioner
    );

    switch (componentToShow) {
        case 'formInput':
            return (
                <CareProviderFormInput
                    data-automation-test={automation}
                    error={error}
                    formlabeltext={formlabeltext}
                    hasErrors={hasErrors}
                    initialOrganizationId={initialOrganizationId}
                    initialPractitionerId={initialPractitionerId}
                    onChange={onChange}
                    onDoValidate={onDoValidate}
                    required={required}
                    size={size}
                />
            );
        case 'generalPractitionerSearch':
            return (
                <GeneralPractitionerSearchWrapper
                    data-automation-test={automation}
                    error={error}
                    formlabeltext={formlabeltext}
                    hasErrors={hasErrors}
                    initialOrganizationId={initialOrganizationId}
                    initialPractitionerId={initialPractitionerId}
                    isInModal={isInModal}
                    itemsPerPage={itemsPerPage}
                    onChange={onChange}
                    onDoValidate={onDoValidate}
                    required={required}
                    size={size}
                />
            );
        default:
            return null;
    }
}

// eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
function determineComponentToShow(
    isUserManager: boolean,
    patient: IPatient | undefined,
    loggedInUserHasGeneralPractitionersInOrganization: boolean,
    loggedInUserorganizationId: string,
    loggedInUserId: string,
    isInModal?: boolean,
    isActiveGeneralPractitioner?: boolean
): ComponentVariants {
    // if there is no patient, or no organizationId, the GP search component will be displayed as is.
    // then it means it's a standalone component used in a form
    if (!loggedInUserId || !loggedInUserorganizationId) {
        return 'formInput';
    }

    if (!patient) {
        if (loggedInUserHasGeneralPractitionersInOrganization) {
            return 'formInput';
        }

        return 'generalPractitionerSearch';
    }

    if (isUserManager) {
        return 'generalPractitionerSearch';
    }

    // patient's GP org provider matches the logged in user's GP org provider and patient's GP is active
    if (isActiveGeneralPractitioner && patient.careProviderOrganizationId === loggedInUserorganizationId) {
        // check if patient's current GP matches the logged in user's GP

        const loggedInUserCareProviderId = `${loggedInUserorganizationId}_${loggedInUserId}`;
        const patientsCareProviderId = `${patient.careProviderOrganizationId}_${patient.careProviderPractitionerId}`;

        if (loggedInUserCareProviderId === patientsCareProviderId && !isInModal) {
            return 'none';
        }
    }

    // If the logged in user is working in a GP practice, a dropdown is shown with all GPs in the organization

    if (loggedInUserHasGeneralPractitionersInOrganization) {
        return 'formInput';
    } else {
        // If the logged in user is not working in a GP practice, the GP search component will be displayed.
        return 'generalPractitionerSearch';
    }
}
